import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
    acc[camelKey] = typeof obj[key] === 'object' ? toCamelCase(obj[key]) : obj[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toSnakeCase);
  return Object.keys(obj).reduce((acc, key) => {
    const snakeKey = key.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`);
    acc[snakeKey] = typeof obj[key] === 'object' ? toSnakeCase(obj[key]) : obj[key];
    return acc;
  }, {});
};

// Build nested tree from flat array
const buildTree = (nodes, mappingMap = {}, parentId = null) => {
  return nodes
    .filter((n) => (n.parent_id ?? null) === parentId)
    .sort((a, b) => a.order - b.order)
    .map((n) => ({
      id: n.id,
      name: n.name,
      order: n.order,
      parentId: n.parent_id ?? null,
      mappedPage: mappingMap[n.id] ?? null,
      children: buildTree(nodes, mappingMap, n.id),
    }));
};

export const menuService = {
  // Get flat node list for the company (admin use)
  async getMenuTree(companyId) {
    try {
      const { data, error } = await supabase
        .from('menu_nodes')
        .select('*')
        .eq('company_id', companyId)
        .order('order', { ascending: true });

      if (error) throw error;
      return buildTree(data || []);
    } catch (error) {
      console.error('menuService.getMenuTree error:', error?.message);
      throw error;
    }
  },

  // Get tree with per-catalog page mappings (viewer/embed use)
  async getMenuWithMappings(companyId, catalogId) {
    try {
      const [nodesRes, mappingsRes] = await Promise.all([
        supabase
          .from('menu_nodes')
          .select('*')
          .eq('company_id', companyId)
          .order('order', { ascending: true }),
        supabase
          .from('menu_node_catalog_pages')
          .select('node_id, page_number')
          .eq('catalog_id', catalogId),
      ]);

      if (nodesRes.error) throw nodesRes.error;
      if (mappingsRes.error) throw mappingsRes.error;

      const mappingMap = {};
      (mappingsRes.data || []).forEach((m) => {
        mappingMap[m.node_id] = m.page_number;
      });

      return buildTree(nodesRes.data || [], mappingMap);
    } catch (error) {
      console.error('menuService.getMenuWithMappings error:', error?.message);
      return [];
    }
  },

  // Create a node
  async createNode({ companyId, parentId = null, name, order = 0 }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('menu_nodes')
        .insert({ company_id: companyId, parent_id: parentId, name, order })
        .select()
        .single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('menuService.createNode error:', error?.message);
      throw error;
    }
  },

  // Update a node (name and/or order)
  async updateNode(nodeId, { name, order }) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updates = {};
      if (name !== undefined) updates.name = name;
      if (order !== undefined) updates.order = order;

      const { data, error } = await supabase
        .from('menu_nodes')
        .update(updates)
        .eq('id', nodeId)
        .select()
        .single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('menuService.updateNode error:', error?.message);
      throw error;
    }
  },

  // Delete a node (cascades to children via FK)
  async deleteNode(nodeId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('menu_nodes')
        .delete()
        .eq('id', nodeId);

      if (error) throw error;
    } catch (error) {
      console.error('menuService.deleteNode error:', error?.message);
      throw error;
    }
  },

  // Reorder siblings: accepts [{ id, order }, ...]
  async reorderNodes(updates) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await Promise.all(
        updates.map(({ id, order }) =>
          supabase.from('menu_nodes').update({ order }).eq('id', id)
        )
      );
    } catch (error) {
      console.error('menuService.reorderNodes error:', error?.message);
      throw error;
    }
  },

  // Save or update a catalog page mapping for a node
  async saveCatalogMapping(nodeId, catalogId, pageNumber) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('menu_node_catalog_pages')
        .upsert(
          { node_id: nodeId, catalog_id: catalogId, page_number: pageNumber },
          { onConflict: 'node_id,catalog_id' }
        )
        .select()
        .single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('menuService.saveCatalogMapping error:', error?.message);
      throw error;
    }
  },

  // Remove the page mapping for a node in a catalog
  async deleteCatalogMapping(nodeId, catalogId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('menu_node_catalog_pages')
        .delete()
        .eq('node_id', nodeId)
        .eq('catalog_id', catalogId);

      if (error) throw error;
    } catch (error) {
      console.error('menuService.deleteCatalogMapping error:', error?.message);
      throw error;
    }
  },

  // Get all mappings for a catalog (flat list)
  async getMappingsByCatalog(catalogId) {
    try {
      const { data, error } = await supabase
        .from('menu_node_catalog_pages')
        .select('node_id, page_number')
        .eq('catalog_id', catalogId);

      if (error) throw error;
      return (data || []).map(toCamelCase);
    } catch (error) {
      console.error('menuService.getMappingsByCatalog error:', error?.message);
      throw error;
    }
  },
};

export default menuService;
